/**
 * Sprint 138 — Cómo se pide la carne.
 *
 * El negocio lo reportó así: *"sucede que la piden en términos: medio, 3/4, bien cocido, la carne
 * bien picada, en rebanadas, en tiras, dorada… y no me da la opción"*. Y el mesero añadió de dónde
 * sale la idea: *"en Toro Bravo tienen un apartado para el término de los cortes"*.
 *
 * ## Por qué botones y no sólo un campo de texto
 *
 * Un término de cocción **no es una nota**: es un atributo de producción con valores cerrados. A
 * las nueve de la noche, con tres mesas esperando, nadie teclea "término medio" en una pantalla
 * táctil — lo escribe mal, lo abrevia o no lo escribe. Un toque no se equivoca, y además hace que
 * la cocina lea siempre la misma palabra.
 *
 * El texto libre se queda igual, para lo que no está en la lista ("sin hueso", "que le quede
 * jugosa"). Los botones son para lo repetido; el teclado, para lo raro. Uno no sustituye al otro.
 *
 * ## Por qué la lista se configura
 *
 * Esta lista nace con lo que dijo el negocio, pero **la termina el negocio**: ya están operando y
 * saben qué les piden de verdad. Un catálogo cerrado obligaría a pedir una versión nueva del
 * sistema cada vez que aparezca una forma de pedir la carne, y eso no ocurre en el ramo.
 */

/** Clave en `configuracion_sistema`. */
export const CLAVE_NOTAS_RAPIDAS = 'notas_rapidas_pedido';

export interface GrupoNotasRapidas {
  /** Cómo se llama el apartado en pantalla: "Término", "Cómo se corta". */
  titulo: string;
  opciones: string[];
}

/**
 * Lo que trae de fábrica, en el vocabulario de un asador.
 *
 * Las palabras son las que usó el negocio, no las de un manual: "bien picada" (para tacos), "en
 * tiras", "dorada". Los términos de cocción van del rojo al bien cocido en el orden en que se
 * dicen, para que la posición del botón se aprenda con la mano.
 *
 * Máximo cinco por apartado a propósito (Hick): con quince botones iguales en pantalla se toca el
 * que queda cerca, no el que se necesita.
 */
export const NOTAS_RAPIDAS_DEFAULT: GrupoNotasRapidas[] = [
  {
    titulo: 'Término',
    opciones: ['Rojo', 'Medio rojo', 'Término medio', 'Tres cuartos', 'Bien cocido'],
  },
  {
    titulo: 'Cómo se corta',
    opciones: ['Bien picada', 'En tiras', 'En rebanadas', 'En trozos', 'Sin hueso'],
  },
  {
    titulo: 'Preparación',
    opciones: ['Dorada', 'Suave', 'Sin sal', 'Sin picante', 'Sin cebolla'],
  },
  {
    titulo: 'Para llevar',
    opciones: ['Salsas aparte', 'Sin cubiertos', 'Empacar por separado'],
  },
];

/**
 * Cuánto texto acepta una nota.
 *
 * El handler admite 500 caracteres; aquí se corta antes por una razón física: una nota de 500
 * caracteres son ~16 renglones de papel térmico **por producto**, y la comanda deja de leerse de
 * un vistazo. 200 alcanza de sobra para "término medio, sin hueso y bien picada".
 */
export const LIMITE_NOTA = 200;

/**
 * Topes de la lista configurable.
 *
 * El handler de configuración rechaza cualquier valor de más de **1000 caracteres**
 * (`configHandlers.ts`), así que el peor caso de esta lista tiene que caber ahí con margen:
 * `4 × (24 + 1 + 8×24 + 7) + 3` = **899**.
 *
 * La primera versión de este sprint tenía 6×10×28 → **1913 caracteres**, casi el doble del tope.
 * No habría fallado en pruebas —la lista de fábrica ocupa 380— sino el día que la dueña terminara
 * de escribir su vocabulario: Guardar le habría contestado *"Valor de configuración inválido"* y
 * habría perdido la captura entera. Lo cazó la auditoría, no los tests, porque los tests medían
 * el caso feliz.
 *
 * Además de los topes hay un guardián duro en `serializarNotasRapidas`: los números pueden volver
 * a desalinearse, el guardián no.
 */
export const LIMITE_GRUPOS = 4;
export const LIMITE_OPCIONES_POR_GRUPO = 8;
export const LIMITE_TEXTO_OPCION = 24;

/** Lo máximo que puede ocupar la lista serializada. Por debajo del tope del handler (1000). */
export const LIMITE_SERIALIZADO = 900;

/** Separadores del formato guardado. No pueden aparecer dentro de un texto. */
const SEP_GRUPO = ';';
const SEP_TITULO = '>';
const SEP_OPCION = '|';

/**
 * Limpia un texto que va a una nota o a la lista configurable.
 *
 * Quita los separadores del formato —que romperían el guardado— y los caracteres de control, que
 * en una impresora térmica **no son texto sino comandos**: un byte suelto puede cambiarle la
 * tipografía, cortar el papel a media comanda o dejarla en blanco. El renderer no es confiable y
 * el navegador del cliente lo es todavía menos, así que esto se aplica también en el servidor.
 */
export function limpiarTextoNota(texto: string, limite = LIMITE_NOTA): string {
  const limpio = (texto ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, ' ')
    .replace(new RegExp(`[${SEP_GRUPO}${SEP_TITULO}${SEP_OPCION}]`, 'g'), ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return recortar(limpio, limite);
}

/**
 * Corta sin partir un carácter por la mitad.
 *
 * `slice` cuenta unidades de UTF-16, así que cortar justo en un emoji —*"bien cocida 🥩"* escrito
 * desde un celular— dejaba **media letra**: un byte suelto que acaba como `?` en el papel y como
 * carácter de reemplazo en la base. Cortar por code points lo evita, y de paso no deja la nota
 * terminando en media palabra tras un espacio.
 */
function recortar(texto: string, limite: number): string {
  if (texto.length <= limite) return texto;
  const puntos = [...texto];
  if (puntos.length <= limite) return texto;
  return puntos.slice(0, limite).join('').trim();
}

/**
 * Lee la lista guardada; ante cualquier cosa rara, devuelve la de fábrica.
 *
 * Tolerante a propósito: una lista mal guardada dejaría la pantalla de notas **sin botones**, y
 * el operador no tendría forma de saber si es que el negocio los quitó o si el sistema falló.
 * Entre mostrar la lista de fábrica y no mostrar nada, se muestra algo que funciona.
 *
 * Una lista vacía guardada a propósito sí se respeta: es el negocio diciendo "aquí sólo se
 * escribe a mano".
 */
export function leerNotasRapidas(valor?: string | null): GrupoNotasRapidas[] {
  const texto = (valor ?? '').trim();
  if (texto === '') return NOTAS_RAPIDAS_DEFAULT;
  // Marca explícita de "el negocio apagó los botones". Sin esto, vaciar la lista la resucitaría.
  if (texto === '-') return [];

  const grupos: GrupoNotasRapidas[] = [];
  for (const crudo of texto.split(SEP_GRUPO)) {
    const [tituloCrudo, opcionesCrudas] = crudo.split(SEP_TITULO);
    const titulo = limpiarTextoNota(tituloCrudo ?? '', LIMITE_TEXTO_OPCION);
    if (!titulo) continue;
    const opciones = (opcionesCrudas ?? '')
      .split(SEP_OPCION)
      .map((o) => limpiarTextoNota(o, LIMITE_TEXTO_OPCION))
      .filter(Boolean)
      .slice(0, LIMITE_OPCIONES_POR_GRUPO);
    if (opciones.length === 0) continue;
    grupos.push({ titulo, opciones });
  }
  return grupos.length > 0 ? grupos.slice(0, LIMITE_GRUPOS) : NOTAS_RAPIDAS_DEFAULT;
}

/** Deja la lista en el texto que se guarda. `-` es la lista vacía deliberada. */
export function serializarNotasRapidas(grupos: GrupoNotasRapidas[]): string {
  const limpios = grupos
    .map((g) => ({
      titulo: limpiarTextoNota(g.titulo, LIMITE_TEXTO_OPCION),
      opciones: g.opciones
        .map((o) => limpiarTextoNota(o, LIMITE_TEXTO_OPCION))
        .filter(Boolean)
        .slice(0, LIMITE_OPCIONES_POR_GRUPO),
    }))
    .filter((g) => g.titulo && g.opciones.length > 0)
    .slice(0, LIMITE_GRUPOS);

  if (limpios.length === 0) return '-';
  const texto = limpios
    .map((g) => `${g.titulo}${SEP_TITULO}${g.opciones.join(SEP_OPCION)}`)
    .join(SEP_GRUPO);

  /*
    El guardián duro: si aun así no cabe, se recorta aquí en vez de que el servidor rechace el
    guardado entero. Perder la última opción es un mal menor; perder la captura completa con un
    mensaje técnico es de los "nunca" (una advertencia sin acción al lado).

    Nunca debería dispararse con los topes de arriba — está por si alguien los vuelve a mover.
  */
  if (texto.length <= LIMITE_SERIALIZADO) return texto;
  return serializarNotasRapidas(
    limpios.map((g, i) =>
      i === limpios.length - 1 ? { ...g, opciones: g.opciones.slice(0, -1) } : g,
    ),
  );
}

/** ¿Cabe esta lista en el ajuste? Lo usa la pantalla para avisar ANTES de guardar. */
export function cabeLaLista(grupos: GrupoNotasRapidas[]): boolean {
  const limpios = grupos.filter((g) => g.titulo.trim() && g.opciones.length > 0);
  const texto = limpios
    .map(
      (g) =>
        `${limpiarTextoNota(g.titulo, LIMITE_TEXTO_OPCION)}${SEP_TITULO}${g.opciones
          .map((o) => limpiarTextoNota(o, LIMITE_TEXTO_OPCION))
          .join(SEP_OPCION)}`,
    )
    .join(SEP_GRUPO);
  return texto.length <= LIMITE_SERIALIZADO;
}

/** Separador con el que se unen los botones elegidos dentro de la nota. */
export const SEPARADOR_NOTA = ' · ';

/**
 * Arma la nota final: primero lo que se tocó, después lo que se escribió.
 *
 * Sprint 139 — **Descarta partes enteras en vez de cortar a media palabra.**
 *
 * Con muchos botones elegidos, el recorte a 200 partía la última opción por la mitad y la comanda
 * salía diciendo *"Término me"*. Peor: al reabrir la nota para corregirla, ese trozo ya no se
 * reconocía como botón y se quedaba de basura en el texto libre.
 *
 * Ahora se van quitando partes completas desde el final hasta que cabe: la nota dice menos, pero
 * lo que dice es cierto. Lo cazó `qa-chaos` con 32 opciones elegidas.
 */
export function componerNota(seleccion: string[], textoLibre: string): string {
  const partes = [...seleccion.map((s) => s.trim()).filter(Boolean)];
  const libre = limpiarTextoNota(textoLibre);
  if (libre) partes.push(libre);

  const vivas = [...partes];
  while (vivas.length > 0) {
    const nota = vivas.join(SEPARADOR_NOTA);
    // `limpiarTextoNota` no corta si ya cabe; se compara contra el texto completo.
    if ([...nota].length <= LIMITE_NOTA) return limpiarTextoNota(nota);
    vivas.pop();
  }
  return '';
}

/**
 * Deshace `componerNota` para poder reabrir una nota ya escrita.
 *
 * Sin esto, corregir una nota obligaría a volver a teclearla entera: el operador abriría la
 * pantalla, vería sus botones apagados y el texto junto en el campo libre. Lo que no reconoce
 * como botón se devuelve como texto, que es la única forma de no perder nada de lo escrito.
 */
export function separarNota(
  nota: string | null | undefined,
  grupos: GrupoNotasRapidas[],
): { seleccion: string[]; textoLibre: string } {
  const partes = (nota ?? '')
    .split(SEPARADOR_NOTA)
    .map((p) => p.trim())
    .filter(Boolean);
  const conocidas = new Set(grupos.flatMap((g) => g.opciones));
  const seleccion: string[] = [];
  const libres: string[] = [];
  for (const p of partes) {
    if (conocidas.has(p)) seleccion.push(p);
    else libres.push(p);
  }
  return { seleccion, textoLibre: libres.join(SEPARADOR_NOTA) };
}
